import {createContext, ReactNode, useContext, useMemo, useSyncExternalStore} from "react";
import {SessionUser, tokenStore} from "./tokenStore";
import {MfaChallenge, MfaMethod, SiteAdministratorClaim} from "../api/models";

interface AuthState {
    user: SessionUser | null;
    // True while a new page renews its session from the cookie: whether anyone's signed in isn't known yet.
    loading: boolean;
    isAdmin: boolean;
    // Null when signed in; a challenge when the account needs a second factor. turnstileToken: the bot check's.
    login(username: string, password: string, turnstileToken: string): Promise<MfaChallenge | null>;
    completeMfa(challenge: MfaChallenge, method: MfaMethod, code: string): Promise<void>;
    logout(): Promise<void>;
    renew(): Promise<unknown>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({children}: { children: ReactNode }) {
    // Follows the session, including sign-ins and sign-outs in other tabs.
    const snapshot = useSyncExternalStore(tokenStore.subscribe, tokenStore.snapshot);
    const state = useMemo<AuthState>(() => {
        const user = tokenStore.user();
        return {
            user,
            loading: tokenStore.status() === 'unknown',
            isAdmin: !!user?.claims?.includes(SiteAdministratorClaim),
            login: (username, password, turnstileToken) => tokenStore.login(username, password, turnstileToken),
            completeMfa: (challenge, method, code) => tokenStore.completeMfa(challenge, method, code),
            logout: () => tokenStore.logout(),
            renew: () => tokenStore.renew()
        };
    }, [snapshot]);
    return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
    const state = useContext(AuthContext);
    if (!state) {
        throw new Error('useAuth needs an AuthProvider');
    }
    return state;
}
